import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listJobs, regenerateJob, unlockJob } from "../api";
import { ShareButtons } from "../components/ShareButtons";
import { StatusBadge } from "../components/StatusBadge";
import { useMe, useSession } from "../state";

type Job = {
  id: string;
  status: string;
  quality: "free" | "paid";
  unlocked: boolean;
  can_unlock: boolean;
  prompt: string;
  moderation_reason: string | null;
  error: string | null;
  result_url: string | null;
  created_at: string;
  regen_of?: string | null;
  regen_count?: number;
};

const TILTS = ["-rotate-1", "rotate-1", "-rotate-[0.5deg]", "rotate-[0.5deg]"];

export function Jobs() {
  const { token } = useSession();
  const { me, refresh: refreshMe } = useMe();
  const nav = useNavigate();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [unlocking, setUnlocking] = useState<string | null>(null);
  const [regenning, setRegenning] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState<string>("");

  const startEdit = (j: Job) => {
    setEditingId(j.id);
    setEditPrompt(j.prompt);
  };
  const cancelEdit = () => { setEditingId(null); setEditPrompt(""); };

  const onRegenerate = async (id: string, prompt?: string) => {
    setRegenning(id);
    try {
      const t = (await token())!;
      await regenerateJob(t, id, prompt);
      cancelEdit();
    } catch (e: any) {
      alert(e.message || "couldn't start regeneration");
    } finally {
      setRegenning(null);
    }
  };

  const onUnlock = async (id: string) => {
    setUnlocking(id);
    try {
      const t = (await token())!;
      const updated = await unlockJob(t, id);
      setJobs((prev) => prev?.map((j) => (j.id === id ? updated : j)) ?? prev);
      refreshMe();
    } catch (e: any) {
      alert(e.message || "couldn't unlock");
    } finally {
      setUnlocking(null);
    }
  };

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const TERMINAL = new Set(["completed", "failed", "moderation_rejected"]);

    const tick = async () => {
      try {
        const t = await token();
        if (!t) return;
        const data: Job[] = await listJobs(t);
        if (!alive) return;
        setJobs(data);
        // Keep polling only while at least one job is in flight.
        const hasInflight = data.some((j) => !TERMINAL.has(j.status));
        if (hasInflight) timer = setTimeout(tick, 3000);
      } catch {
        if (alive) timer = setTimeout(tick, 5000);
      }
    };
    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [token]);

  if (jobs === null) {
    return <p className="mt-12 text-center text-ink-soft animate-pulse">loading…</p>;
  }

  if (jobs.length === 0) {
    return (
      <div className="mt-16 text-center">
        <p className="font-display text-2xl mb-3">no live photos yet</p>
        <p className="text-ink-soft mb-6">your first one is one photo away.</p>
        <Link to="/" className="btn-primary">submit a photo</Link>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h2 className="font-display font-black text-3xl mb-6">your live photos</h2>
      <ul className="grid sm:grid-cols-2 gap-6">
        {jobs.map((j, i) => (
          <li
            key={j.id}
            className={`bg-white shadow-polaroid rounded-polaroid p-3 pb-5 ${TILTS[i % TILTS.length]} hover:rotate-0 transition-transform duration-300`}
          >
            {j.result_url ? (
              <video
                src={j.result_url}
                controls
                playsInline
                className="block w-full max-h-[70vh] rounded-sm bg-ink"
              />
            ) : (
              <div className="aspect-video bg-cream-dark rounded-sm grid place-items-center text-ink-mute text-sm">
                <div className="flex flex-col items-center gap-2">
                  <StatusBadge status={j.status} />
                  {j.status === "running" && <span className="text-xs">this can take a few minutes</span>}
                </div>
              </div>
            )}
            <div className="px-1 pt-3 flex items-start justify-between gap-3">
              <p className="font-display text-base leading-snug line-clamp-2">{j.prompt}</p>
              <span className="text-[10px] uppercase tracking-wider text-ink-mute mt-1 shrink-0">
                {new Date(j.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
            {j.result_url && j.status === "completed" && (
              <ShareButtons jobId={j.id} videoUrl={j.result_url} />
            )}
            {j.status === "completed" && !j.regen_of && (j.regen_count ?? 0) < 1 && (
              editingId === j.id ? (
                <div className="mt-2 px-1 space-y-2">
                  <textarea
                    className="textarea text-sm h-24 resize-none"
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    maxLength={2000}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => onRegenerate(j.id, editPrompt.trim() || undefined)}
                      disabled={regenning === j.id || !editPrompt.trim()}
                      className="btn-primary text-xs px-3 py-1.5"
                    >
                      {regenning === j.id ? "starting…" : "↻ regenerate"}
                    </button>
                    <button onClick={cancelEdit} className="btn-ghost text-xs px-3 py-1.5 bg-cream-dark">
                      cancel
                    </button>
                  </div>
                  <p className="text-[11px] text-ink-mute">free — once per render. tweaks the prompt and rolls a new seed.</p>
                </div>
              ) : (
                <button
                  onClick={() => startEdit(j)}
                  className="btn-ghost text-xs px-3 py-1.5 mt-2"
                  title="render again with a different seed — free, once per render"
                >
                  ↻ regenerate (1 free try)
                </button>
              )
            )}
            {j.regen_of && (
              <p className="mt-2 text-[11px] text-ink-mute px-1">↻ regeneration of an earlier render</p>
            )}
            {j.can_unlock && (
              <div className="mt-3 flex items-center justify-between gap-2 px-1">
                <span className="text-xs text-ink-soft">watermarked preview</span>
                <button
                  onClick={() => onUnlock(j.id)}
                  disabled={unlocking === j.id || (me?.tokens ?? 0) < 1}
                  className="btn-primary text-xs px-3 py-1.5"
                  title={(me?.tokens ?? 0) < 1 ? "need 1 token" : ""}
                >
                  {unlocking === j.id
                    ? "unlocking…"
                    : (me?.tokens ?? 0) < 1
                    ? "1 token to unlock"
                    : "unlock — 1 token"}
                </button>
              </div>
            )}
            {j.moderation_reason && (
              <div className="mt-2 px-1">
                <p className="text-xs text-coral-dark">rejected: {j.moderation_reason}</p>
                <p className="text-[11px] text-ink-mute mt-1">no token spent — try a different prompt or photo.</p>
                <button
                  onClick={() => nav("/", { state: { prompt: j.prompt } })}
                  className="btn-primary text-xs px-3 py-1.5 mt-2"
                >
                  tweak and try again
                </button>
              </div>
            )}
            {j.error && (
              <p className="mt-2 text-xs text-coral-dark px-1">{j.error}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
